import type { DespachoSnapshot, FormaPago } from '@conversia/shared-types';
import type { JobWithRelations } from '@/api/jobs.api';

/**
 * Render de la FICHA DE DESPACHO en texto plano, con EXACTAMENTE el formato que
 * el equipo arma a mano cada noche (ejemplo real del cliente):
 *
 *   Viernes 11 Julio Hora: 3:30 PM
 *   18095551234
 *   RNC: Sí
 *   Vendedor: María
 *   - Pago: Efectivo
 *   - Tecnico: Pedro
 *   - Compañia: Negocio Demo
 *   - Modelo: U7 RD$14,995
 *   - Ubicacion: https://maps.google.com/?q=...
 *   SDE
 *
 * Regla de oro: un campo sin dato sale como "—" — NUNCA se inventa. Lo
 * COMERCIAL sale del snapshot (despachoJson); lo OPERATIVO (cita, técnico,
 * zona) sale VIVO del Job (una reprogramación no desincroniza la ficha).
 * Fechas/hora en es-DO con la zona horaria del navegador (el panel se opera
 * desde RD).
 */

export const FICHA_EMPTY = '—';

export const PAGO_LABELS: Record<FormaPago, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA: 'Transferencia',
  MIXTO: 'Mixto',
};

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** "Viernes" / "11 Julio" / "3:30 PM" desde el scheduledAt (o "—" sin cita). */
export function fichaFechaParts(scheduledAt: string | null | undefined): {
  dia: string;
  fecha: string;
  hora: string;
} {
  if (!scheduledAt) return { dia: FICHA_EMPTY, fecha: FICHA_EMPTY, hora: FICHA_EMPTY };
  const d = new Date(scheduledAt);
  if (Number.isNaN(d.getTime())) return { dia: FICHA_EMPTY, fecha: FICHA_EMPTY, hora: FICHA_EMPTY };
  const dia = cap(d.toLocaleDateString('es-DO', { weekday: 'long' }));
  const fecha = `${d.getDate()} ${cap(d.toLocaleDateString('es-DO', { month: 'long' }))}`;
  const hora = d
    .toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit', hour12: true })
    // Intl mete NBSP/narrow-NBSP antes del meridiano segun la version de ICU.
    .replace(/[\u202f\u00a0]/g, ' ')
    .replace(/a\.?\s?m\.?/i, 'AM')
    .replace(/p\.?\s?m\.?/i, 'PM');
  return { dia, fecha, hora };
}

/** El texto plano de UNA ficha, listo para copiar al grupo de WhatsApp. */
export function renderFichaDespacho(job: JobWithRelations, currencyPrefix = 'RD$'): string {
  const snap = (job.despachoJson ?? null) as DespachoSnapshot | null;
  const { dia, fecha, hora } = fichaFechaParts(job.scheduledAt);

  const telefono = snap?.telefono ?? job.contact?.phone ?? FICHA_EMPTY;
  const rnc =
    snap?.comprobanteFiscal === 'si' ? 'Sí' : snap?.comprobanteFiscal === 'no' ? 'No' : FICHA_EMPTY;
  const vendedor = snap?.vendedor ?? FICHA_EMPTY;
  const pago = snap?.formaPago ? PAGO_LABELS[snap.formaPago] : FICHA_EMPTY;
  const tecnico = job.employee?.nombre ?? FICHA_EMPTY;
  const compania = snap?.compania ?? FICHA_EMPTY;
  const modelo = snap?.modelo ?? FICHA_EMPTY;
  const precio =
    snap?.precio != null ? `${currencyPrefix}${snap.precio.toLocaleString('en-US')}` : FICHA_EMPTY;
  const ubicacion = snap?.mapsUrl ?? FICHA_EMPTY;
  const zona = (job.zona ?? snap?.zona ?? FICHA_EMPTY).toUpperCase();

  return [
    `${dia} ${fecha} Hora: ${hora}`,
    telefono,
    `RNC: ${rnc}`,
    `Vendedor: ${vendedor}`,
    `- Pago: ${pago}`,
    `- Tecnico: ${tecnico}`,
    `- Compañia: ${compania}`,
    `- Modelo: ${modelo} ${precio}`,
    `- Ubicacion: ${ubicacion}`,
    zona,
  ].join('\n');
}

/** Todas las fichas del día, separadas por línea en blanco ("Copiar todas"). */
export function renderFichasDespacho(jobs: JobWithRelations[], currencyPrefix = 'RD$'): string {
  return jobs.map((j) => renderFichaDespacho(j, currencyPrefix)).join('\n\n');
}
